/**
 * SHARED CONSTANTS AND CONFIGURATION
 * Shared between web app and workers
 */

export enum IndexSymbol {
    NIFTY = 'NIFTY',
    SENSEX = 'SENSEX',
}

export enum OptionType {
    CE = 'CE',
    PE = 'PE',
}

export enum AnalysisMode {
    OI = 'OI',
    VOLUME = 'VOLUME',
}

export enum TimeGap {
    M1 = '1m',
    M5 = '5m',
    M10 = '10m',
    M15 = '15m',
    M30 = '30m',
    H1 = '1h',
    H2 = '2h',
    H4 = '4h',
}

export enum UserRole
{
    ADMIN = 'admin',
    USER = 'user',
}

export enum UserStatus
{
    PENDING = 'pending',
    ACTIVE = 'approved',
    REJECTED = 'rejected',
}

// ============================================================================
// 1. SYMBOLS CONFIGURATION
// ============================================================================
export const SUPPORTED_SYMBOLS = [IndexSymbol.NIFTY, IndexSymbol.SENSEX] as const;

export const SYMBOL_DISPLAY_NAMES: Record<IndexSymbol, string> = {
    [IndexSymbol.NIFTY]: 'NIFTY',
    [IndexSymbol.SENSEX]: 'SENSEX',
};

export const SYMBOL_METADATA: Record<IndexSymbol, { hasHistoricalData: boolean }> = {
    [IndexSymbol.NIFTY]: { hasHistoricalData: true },
    [IndexSymbol.SENSEX]: { hasHistoricalData: true },
};

export const SYMBOL_VARIANTS: Record<string, string[]> = {
    [IndexSymbol.NIFTY]: ['NIFTY 50', 'NIFTY50', 'NSE_INDEX|Nifty 50', 'NSE_INDEX|NIFTY'],
    [IndexSymbol.SENSEX]: ['BSE_INDEX|SENSEX', 'BSESENSEX'],
};

// ============================================================================
// 2. API CONFIGURATION
// ============================================================================

export const UPSTOX_API_CONFIG = {
    REAL_URL: 'https://service.upstox.com/option-analytics-tool/open/v1/strategy-chains',
    CANDLE_BASE_URL: 'https://api.upstox.com/v3/historical-candle',
    symbols: {
        [IndexSymbol.NIFTY]: {
            assetKey: 'NSE_INDEX|Nifty 50',
            assetKeyEncoded: 'NSE_INDEX%7CNifty+50',
            displayName: 'NIFTY',
        },
        [IndexSymbol.SENSEX]: {
            assetKey: 'BSE_INDEX|SENSEX',
            assetKeyEncoded: 'BSE_INDEX%7CSENSEX',
            displayName: 'SENSEX',
        },
    }
};

export const UPSTOX_INSTRUMENT_KEYS: Record<string, string> = {
    NIFTY: 'NSE_INDEX|Nifty 50',
    SENSEX: 'BSE_INDEX|SENSEX',
};

export const HF_CONFIG = {
    REPO: 'bathinimallikarjuna637/Options-Oracle',
    BASE_API_URL: 'https://huggingface.co/api/datasets/bathinimallikarjuna637/Options-Oracle/tree/main',
    BASE_RESOLVE_URL: 'https://huggingface.co/datasets/bathinimallikarjuna637/Options-Oracle/resolve/main',
    TOKEN: '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',
};

export const API_CONFIG = {
    SOURCES: {
        EXPIRY_CSV: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQpDYt-WXR1FfLdVbWUmgjbgsFJYlJZvTiXBvBmXqe0JZuQ65f2hLiUtrmN4MLm5xycG9aC5oQ2kHFO/pub?output=csv',
        INTRADAY_SHEET_LINKS: {
            [IndexSymbol.NIFTY]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSwBaUIM16XND5CdYgmpbwf9OHlJeaV7dS93BqSgQTHPLNQU9ArgvKC6fL8_avGeF2qH9ahEtmBYRX7/pub?gid=1903702304&single=true&output=csv',
            [IndexSymbol.SENSEX]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRPG1FFd07Tzo3aApcHAXyrhdRRnQnrZXYO1tR_9p7aa1fpAvokkzKuUBvNxfKM5cz90PbjL-T_pBF4/pub?gid=1903702304&single=true&output=csv',
        },
        FALLBACK_GROWW_CSV: 'https://growwapi-assets.groww.in/instruments/instrument.csv',
        HUGGING_FACE_REPO: 'bathinimallikarjuna637/Options-Oracle',
        HUGGING_FACE_TOKEN: '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',
    },
    CSV_CONFIG: {
        NUMERIC_FIELDS: [
            'strikePrice',
            'callPrice',
            'callChangePercent',
            'callOpenInterest',
            'callVolume',
            'callImpliedVolatility',
            'callChange',
            'callOpenInterestChange',
            'putPrice',
            'putChangePercent',
            'putOpenInterest',
            'putVolume',
            'putImpliedVolatility',
            'putChange',
            'putOpenInterestChange',
            'Index_lastPrice',
            'VIX_value',
        ],
        HEADER_MAPPINGS: {
            strike: 'strikePrice',
            symbol: 'underlyingName',
            expiry: 'expirationDate',
            call_change_p: 'callChangePercent',
            call_oi_chg: 'callOpenInterestChange',
            put_change_p: 'putChangePercent',
            put_oi_chg: 'putOpenInterestChange',
        },
    },
};

// ============================================================================
// 3. STRIKE GAP
// ============================================================================
export const STRIKE_STEP: Record<IndexSymbol, number> = {
    [IndexSymbol.NIFTY]: 50,
    [IndexSymbol.SENSEX]: 100,
};

export function getCanonicalSymbol ( symbol: string ): IndexSymbol | string {
    const s = symbol ? symbol.replace( /\s+/g, '' ).toUpperCase() : ''
    if ( s === 'NIFTY' || s === 'NIFTY50' || s === 'NSE_INDEX|NIFTY50' ) return IndexSymbol.NIFTY
    if ( s === 'SENSEX' || s === 'BSESENSEX' || s === 'BSE_INDEX|SENSEX' ) return IndexSymbol.SENSEX
    return symbol
}

export function getStrikeStepForSymbol ( symbol: IndexSymbol | string ): number {
    const canonical = getCanonicalSymbol( typeof symbol === 'string' ? symbol : symbol )
    return STRIKE_STEP[ canonical as IndexSymbol ] || 50
}

export function normalizeSymbolLookupKey ( value: string ): string {
    return String( value || '' )
        .replace( /[^A-Za-z0-9]/g, '' )
        .toUpperCase()
}

export function getSymbolLookupCandidates ( symbol: string ): string[] {
    const canonical = String( getCanonicalSymbol( symbol ) )
    const symbolConfig = ( UPSTOX_API_CONFIG.symbols as Record<string, { assetKey: string; displayName: string }> )[
        canonical
    ]

    const aliases = new Set<string>( [
        symbol,
        canonical,
        ...( SYMBOL_VARIANTS[ canonical ] || [] ),
        ...( symbolConfig ? [ symbolConfig.assetKey, symbolConfig.displayName ] : [] ),
        ...( UPSTOX_INSTRUMENT_KEYS[ canonical ] ? [ UPSTOX_INSTRUMENT_KEYS[ canonical ] ] : [] )
    ] )
    const aliasesList: string[] = []
    for ( const a of aliases as any )
    {
        aliasesList.push( a )
    }
    return aliasesList
}

export function isSymbolLookupMatch ( candidateKey: string, symbol: string ): boolean {
    const normalizedCandidate = normalizeSymbolLookupKey( candidateKey )
    if ( !normalizedCandidate ) return false

    const possibleKeys = getSymbolLookupCandidates( symbol )
    let possibleList: string[] = []
    for (const p of possibleKeys as any) possibleList.push(p)

    for ( const k of possibleList )
    {
        const normalized = normalizeSymbolLookupKey( k )
        if ( normalized && normalizedCandidate.includes( normalized ) ) return true
    }

    return false
}

// ============================================================================
// 4. COLORS
// ============================================================================
export const COLORS = {
    SUPPORT: '#10b981',
    RESISTANCE: '#ef4444',
    NEUTRAL: '#6b7280',
    TEXT: { POSITIVE: '#059669', NEGATIVE: '#dc2626', DEFAULT: '#111827' },
};

// ============================================================================
// 5. MARKET HOURS (Shared Logic)
// ============================================================================
export const MARKET_HOURS = {
    OPEN_HOUR: 9,
    OPEN_MINUTE: 13,
    CLOSE_HOUR: 15,
    CLOSE_MINUTE: 32,
    TIMEZONE: 'Asia/Kolkata',
    TRADING_DAYS: [ 1, 2, 3, 4, 5 ],
}

export function isMarketDay(date?: Date): boolean {
    const d = date || new Date();
    // Use Intl for cross-platform TZ support if available, otherwise fallback
    try {
        const istDate = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const day = istDate.getDay();
        return day !== 0 && day !== 6;
    } catch (e) {
        const day = d.getDay();
        return day !== 0 && day !== 6;
    }
}

export function isMarketHours (): boolean
{
    if ( !isMarketDay() ) return false
    const now = new Date()
    // Same approach for IST
    try {
        const istNow = new Date( now.toLocaleString( 'en-US', { timeZone: 'Asia/Kolkata' } ) )
        const hours = istNow.getHours()
        const minutes = istNow.getMinutes()
        const timeInMinutes = hours * 60 + minutes
        return timeInMinutes >= 553 && timeInMinutes <= 932 // 09:13 to 15:32
    } catch (e) {
        return false
    }
}

export const REFRESH_INTERVALS = {
    LIVE_DATA: 2000,
    BACKUP: 60000, // 1 minute — per user rules (backup every 1 min for all symbols/expiries)
    EXPIRY_UPDATE: 86400000,
}

// ============================================================================
// 6. STORAGE KEYS
// ============================================================================
export const STORAGE_KEYS = {
    USER_SETTINGS: 'oracle_user_settings',
    USER_STATE: 'oracle_user',
    STRIKE_HISTORY_ROWS: 'oracle_strike_history_rows',
    LIVE_STRIKE_ROWS: 'oracle_live_strike_rows',
    LIVE_STRIKE_COLUMNS: 'oracle_live_strike_columns',
    HOME_COLUMNS: 'oracle_home_columns',
    INSPECTOR_COLUMNS: 'oracle_inspector_columns',
    INSPECTOR_ROWS: 'oracle_inspector_rows',
    COOKIE_CONSENT: 'oracle_cookie_consent',
    DASHBOARD_ROWS: 'oracle_dashboard_rows',
    DASHBOARD_RANKING_ROWS: 'oracle_dashboard_ranking_rows',
    DASHBOARD_RANKING_COLUMNS: 'oracle_dashboard_ranking_columns',
    DASHBOARD_HISTORY_COLUMNS: 'oracle_dashboard_history_columns',
    VARIANCE_HISTORY_ROWS: 'oracle_variance_history_rows',
    VARIANCE_HISTORY_COLUMNS: 'oracle_variance_history_columns',
    CROSSOVER_HISTORY_ROWS: 'oracle_crossover_history_rows',
    CROSSOVER_HISTORY_COLUMNS: 'oracle_crossover_history_columns',
    CROSSOVER_COLUMNS: 'oracle_crossover_columns_v2',
    VARIANCE_COLUMNS: 'oracle_variance_columns_v2',
    RSI_TABLE_COLUMNS: 'oracle_rsi_table_columns',
    RSI_TABLE_ROWS: 'oracle_rsi_table_rows',
}
